import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  formatRelative,
  playlistWatchUrl,
  useAdminYoutubePlaylists,
  useCreatePlaylist,
} from "../../../lib/youtube";
import { Modal } from "../../../components/ui/Modal";

/**
 * Series (playlists) management list. Each row links to the per-series
 * editor page for inline title/description/privacy edits + drag-reorder
 * of items + add/remove videos.
 *
 * "+ New series" opens a small modal that creates a private playlist and
 * routes to its editor on success.
 */

export default function Series() {
  const [params, setParams] = useSearchParams();
  const q = params.get("q") ?? "";
  const [qInput, setQInput] = useState(q);
  const [creating, setCreating] = useState(false);

  const setParam = (key: string, value: string | null) => {
    const next = new URLSearchParams(params);
    if (value === null || value === "") next.delete(key);
    else next.set(key, value);
    setParams(next, { replace: true });
  };

  const onSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setParam("q", qInput.trim() || null);
  };

  const queryRes = useAdminYoutubePlaylists({ q });
  const playlists = queryRes.data?.playlists ?? [];
  const isLoading = queryRes.isLoading;

  return (
    <div>
      <div className="yt-admin-toolbar">
        <form onSubmit={onSearch} className="yt-search">
          <input
            type="search"
            value={qInput}
            onChange={(e) => setQInput(e.target.value)}
            placeholder="Search series titles…"
            aria-label="Search series"
          />
          <button type="submit" className="btn-app-ghost">
            Search
          </button>
        </form>
        <button
          type="button"
          className="btn-app yt-toolbar-cta"
          onClick={() => setCreating(true)}
        >
          + New series
        </button>
        <p className="yt-admin-count">
          {isLoading ? "Loading…" : `${playlists.length} series`}
        </p>
      </div>

      {isLoading ? (
        <p className="yt-admin-empty mt-6">Loading…</p>
      ) : playlists.length === 0 ? (
        <p className="yt-admin-empty mt-6">
          {q ? "No series match." : "No series yet."}
        </p>
      ) : (
        <ul className="yt-row-list mt-4">
          {playlists.map((p) => (
            <li key={p.id} className="yt-row">
              <Link
                to={`/dashboard/youtube/series/${p.id}`}
                className="yt-row-thumb"
                aria-label={`Edit ${p.title}`}
              >
                {p.thumbnailUrl ? (
                  <img
                    src={p.thumbnailUrl}
                    alt=""
                    loading="lazy"
                    decoding="async"
                  />
                ) : (
                  <span className="yt-row-thumb-empty">—</span>
                )}
                <span className="yt-row-thumb-stack" aria-hidden />
              </Link>

              <div className="yt-row-body">
                <Link
                  to={`/dashboard/youtube/series/${p.id}`}
                  className="yt-row-title font-display-italic"
                >
                  {p.title}
                </Link>
                <p className="yt-row-meta">
                  <span
                    className={`yt-row-privacy ${p.privacyStatus === "public" ? "is-public" : "is-private"}`}
                  >
                    {p.privacyStatus ?? "?"}
                  </span>
                  <span className="yt-row-sep">·</span>
                  <span>
                    <span className="tabular">{p.itemCount}</span>{" "}
                    {p.itemCount === 1 ? "video" : "videos"}
                  </span>
                  <span className="yt-row-sep">·</span>
                  <span>updated {formatRelative(p.updatedAt)}</span>
                </p>
                {p.description && (
                  <p className="yt-row-desc">{p.description}</p>
                )}
              </div>

              <div className="yt-row-actions">
                <Link
                  to={`/dashboard/youtube/series/${p.id}`}
                  className="row-action"
                >
                  Edit
                </Link>
                <a
                  href={playlistWatchUrl(p.id)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="row-action"
                >
                  Open
                </a>
              </div>
            </li>
          ))}
        </ul>
      )}

      {creating && (
        <CreateSeriesModal onClose={() => setCreating(false)} />
      )}
    </div>
  );
}

function CreateSeriesModal({ onClose }: { onClose: () => void }) {
  const create = useCreatePlaylist();
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [privacy, setPrivacy] = useState<"private" | "unlisted" | "public">(
    "private",
  );
  const [error, setError] = useState<string | null>(null);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!title.trim()) {
      setError("Title is required.");
      return;
    }
    try {
      const created = await create.mutateAsync({
        title: title.trim(),
        description: description.trim(),
        privacyStatus: privacy,
      });
      window.location.assign(`/dashboard/youtube/series/${created.id}`);
    } catch (e) {
      setError(
        e instanceof Error ? e.message : "Couldn't create the series.",
      );
    }
  };

  return (
    <Modal
      open
      onClose={onClose}
      title="New series"
      eyebrow="Series · create"
      size="md"
    >
      <form onSubmit={submit} className="yt-form">
        <div className="auth-field">
          <label className="auth-label">Title</label>
          <input
            type="text"
            className="auth-input"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            maxLength={150}
            autoFocus
            required
          />
        </div>
        <div className="auth-field">
          <label className="auth-label">Description (optional)</label>
          <textarea
            className="auth-input"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={3}
            maxLength={5000}
          />
        </div>
        <div className="auth-field">
          <label className="auth-label">Privacy</label>
          <div className="yt-radio-group">
            {(["private", "unlisted", "public"] as const).map((p) => (
              <label key={p} className="yt-radio-row">
                <input
                  type="radio"
                  name="privacy"
                  value={p}
                  checked={privacy === p}
                  onChange={() => setPrivacy(p)}
                />
                <span className="yt-radio-label">
                  <strong>{p}</strong>
                </span>
              </label>
            ))}
          </div>
        </div>
        {error && (
          <p className="yt-admin-error" role="alert">
            {error}
          </p>
        )}
        <div className="yt-form-action-bar">
          <button
            type="submit"
            className="btn-app"
            disabled={create.isPending}
          >
            {create.isPending ? "Creating…" : "Create series"}
          </button>
          <button
            type="button"
            className="btn-app-ghost"
            onClick={onClose}
            disabled={create.isPending}
          >
            Cancel
          </button>
        </div>
      </form>
    </Modal>
  );
}
